import React, { useState } from "react";
import { ArrowRight, X, CheckCircle, AlertCircle, Eye, EyeOff } from "lucide-react";
import { ReactComponent as LogoutIcon } from "../../../../assets/icons/svg/logout.svg";
import { useNavigate } from "react-router-dom";
import { API_URLS } from "../../../api/urls";
import AuthUtils from "../../../utils/auth.utils";

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [toasts, setToasts] = useState<Toast[]>([]);
  
  // Password visibility states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // State management for API flow
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // Get admin_id from AuthUtils
  const getAdminId = () => {
    const adminId = AuthUtils.getAdminId();
    return adminId ? Number(adminId) : null;
  };

  // Handle logout
  const handleLogout = () => {
    AuthUtils.clearSession();
    navigate('/login', { replace: true });
  };

  // Toast notification system
  const showToast = (message: string, type: 'success' | 'error') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 5000);
  };

  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // API 1: Send OTP
  const handleSendOtp = async () => {
    if (!currentPassword) {
      showToast("Please enter your current password", "error");
      return;
    }

    const adminId = getAdminId();
    if (!adminId) {
      showToast("Admin ID not found. Please login again.", "error");
      return;
    }

    setIsSendingOtp(true);
    try {
      const response = await fetch(API_URLS.GET_OTP_TO_CHANGE_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          admin_id: adminId,
          current_password: currentPassword
        })
      });

      const data = await response.json();

      if (data.success === 1) {
        showToast(data.message, "success");
        setIsOtpSent(true);
      } else {
        showToast(data.message || "Failed to send OTP", "error");
      }
    } catch (error) {
      showToast("Error sending OTP. Please try again.", "error");
      console.error('Send OTP error:', error);
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];
      
      if (otp[index]) {
        // If current field has value, clear it
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        // If current field is empty, go to previous field and clear it
        newOtp[index - 1] = "";
        setOtp(newOtp);
        const prevInput = document.getElementById(`otp-${index - 1}`);
        prevInput?.focus();
      }
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    
    // Check if pasted data is 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      
      // Focus the last input
      const lastInput = document.getElementById(`otp-5`);
      lastInput?.focus();
    }
  };

  // API 2: Verify OTP
  const handleVerifyOtp = async () => {
    const otpString = otp.join("");
    
    if (otpString.length !== 6) {
      showToast("Please enter complete OTP", "error");
      return;
    }

    const adminId = getAdminId();
    if (!adminId) {
      showToast("Admin ID not found. Please login again.", "error");
      return;
    }

    setIsVerifyingOtp(true);
    try {
      const response = await fetch(API_URLS.VERIFY_OTP_TO_CHANGE_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          admin_id: adminId,
          otp: otpString
        })
      });

      const data = await response.json();

      if (data.success === 1) {
        showToast(data.message, "success");
        setIsOtpVerified(true);
      } else {
        showToast(data.message || "Invalid OTP", "error");
      }
    } catch (error) {
      showToast("Error verifying OTP. Please try again.", "error");
      console.error('Verify OTP error:', error);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleResendCode = async () => {
    setOtp(["", "", "", "", "", ""]);
    await handleSendOtp();
  };

  // Validate password
  const validatePassword = () => {
    setPasswordError("");

    if (!newPassword || !confirmPassword) {
      setPasswordError("Please fill in both password fields");
      return false;
    }

    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return false;
    }

    if (newPassword === currentPassword) {
      setPasswordError("New password cannot be the same as current password");
      return false;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return false;
    }

    return true;
  };

  // Check if passwords match and are valid for button color change
  const isPasswordValid = () => {
    return (
      isOtpVerified &&
      newPassword.length > 0 &&
      confirmPassword.length > 0
    );
  };

  // API 3: Update Password
  const handleUpdatePassword = async () => {
    if (!validatePassword()) {
      showToast(passwordError, "error");
      return;
    }

    const adminId = getAdminId();
    if (!adminId) {
      showToast("Admin ID not found. Please login again.", "error");
      return;
    }

    setIsUpdatingPassword(true);
    try {
      const response = await fetch(API_URLS.CHANGE_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          admin_id: adminId,
          new_password: newPassword,
          confirm_password: confirmPassword
        })
      });

      const data = await response.json();

      if (data.success === 1) {
        showToast(data.message, "success");
        // Reset form after successful password change
        setTimeout(() => {
          handleCancel();
        }, 2000);
      } else {
        showToast(data.message || "Failed to update password", "error");
      }
    } catch (error) {
      showToast("Error updating password. Please try again.", "error");
      console.error('Update password error:', error);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleCancel = () => {
    setCurrentPassword("");
    setOtp(["", "", "", "", "", ""]);
    setNewPassword("");
    setConfirmPassword("");
    setIsOtpSent(false);
    setIsOtpVerified(false);
    setPasswordError("");
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-6 bg-white min-h-screen relative">
      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`flex items-center gap-3 min-w-[320px] px-4 py-3 rounded-lg shadow-lg ${
              toast.type === 'success'
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            } animate-slide-in`}
          >
            {toast.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            )}
            <p className={`text-sm flex-1 ${
              toast.type === 'success' ? 'text-green-800' : 'text-red-800'
            }`}>
              {toast.message}
            </p>
            <button
              onClick={() => removeToast(toast.id)}
              className="flex-shrink-0"
            >
              <X className={`w-4 h-4 ${
                toast.type === 'success' ? 'text-green-600' : 'text-red-600'
              }`} />
            </button>
          </div>
        ))}
      </div>

      {/* Header with Logout Button */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-semibold text-black">
          Settings
        </h1>
        
        {/* Logout Button */}
        <button
          onClick={() => setShowLogoutConfirm(true)}
          className="flex items-center gap-2 px-2 py-2 text-[#050505] group"
          title="Logout"
        >
          <LogoutIcon className="w-5 h-5" />
          <span className="text-sm font-medium mb-1">Logout</span>
        </button>
      </div>

      {/* Change Password Card */}
      <div className="max-w-[460px]">
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 sm:p-6">
          <div>
            <h2 className="text-lg sm:text-lg font-semibold text-[#111C2D] mb-1">
              Change Password
            </h2>
            <p className="text-sm text-[#050505] mb-4">
              To change your password please confirm here
            </p>

            {/* Current Password */}
            <div className="mb-4">
              <label className="block text-sm font-normal text-black mb-2.5">
                Current Password
              </label>
              <div className="relative w-full">
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  disabled={isOtpSent}
                  className="w-full px-4 py-2.5 pr-24 bg-white border border-[#111C2D1A] rounded-lg text-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
                  placeholder="Enter current password"
                />

                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  disabled={isOtpSent}
                  className="absolute right-14 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors disabled:text-gray-400"
                >
                  {showCurrentPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>

                <button
                  onClick={handleSendOtp}
                  disabled={isOtpSent || isSendingOtp}
                  className={`absolute right-1 top-1/2 -translate-y-1/2 px-2 py-1.5 text-white rounded-md flex items-center justify-center disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors ${
                    currentPassword.length >= 6 && !isOtpSent
                      ? 'bg-black hover:bg-gray-800'
                      : 'bg-[#05050580] hover:bg-[#050505]'
                  }`}
                >
                  {isSendingOtp ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <ArrowRight className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Verify Email OTP */}
            <div className="mb-4">
              <label className="block text-sm font-normal text-black mb-3">
                Verify registered email
              </label>
              <div className="flex gap-2.5 mb-4">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={handleOtpPaste}
                    onFocus={(e) => e.target.select()}
                    disabled={!isOtpSent || isOtpVerified}
                    className="w-11 h-11 sm:w-12 sm:h-12 text-center text-base font-medium bg-white border border-[#CFCFCF] rounded-lg disabled:bg-gray-50 disabled:cursor-not-allowed focus:border-[#3B82F6] focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-opacity-20"
                  />
                ))}
              </div>
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={handleVerifyOtp}
                  disabled={!isOtpSent || isOtpVerified || isVerifyingOtp}
                  className="px-7 py-2 border border-[#05050580] rounded-lg text-sm font-normal text-black hover:bg-gray-50 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400"
                >
                  {isVerifyingOtp ? 'Verifying...' : isOtpVerified ? 'Verified' : 'Verify'}
                </button>
                <div className="text-sm">
                  <span className="text-gray-500">Didn't get the code? </span>
                  <button
                    onClick={handleResendCode}
                    disabled={!isOtpSent || isOtpVerified || isSendingOtp}
                    className="text-[#3B82F6] hover:text-[#2563EB] font-normal disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    {isSendingOtp ? 'Sending...' : 'Resend'}
                  </button>
                </div>
              </div>

              {/* Separator Line */}
              <div className="border-t border-[#E5E5E5] mb-4"></div>

              {/* Create New Password */}
              <div>
                <h3 className="text-base font-semibold text-black mb-4">
                  Create new password
                </h3>
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-normal text-[#111C2D99] mb-2">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          setPasswordError("");
                        }}
                        disabled={!isOtpVerified}
                        className="w-full px-4 py-2.5 pr-12 bg-white border border-[#111C2D99] rounded-lg text-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
                        placeholder="Minimum 6 characters"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        disabled={!isOtpVerified}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors disabled:text-gray-400"
                      >
                        {showNewPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-normal text-[#111C2D99] mb-2">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          setPasswordError("");
                        }}
                        disabled={!isOtpVerified}
                        className="w-full px-4 py-2.5 pr-12 bg-white border border-[#111C2D99] rounded-lg text-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
                        placeholder="Re-enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        disabled={!isOtpVerified}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors disabled:text-gray-400"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  {passwordError && (
                    <p className="text-sm text-red-600">{passwordError}</p>
                  )}
                </div>
                <div className="flex gap-4">
                  <button
                    onClick={handleCancel}
                    disabled={isUpdatingPassword}
                    className="flex px-10 py-2.5 border border-[#FF6692] bg-white rounded-lg text-sm font-normal text-[#FF6692] hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdatePassword}
                    disabled={!isOtpVerified || isUpdatingPassword}
                    className={`flex px-12 py-2.5 rounded-lg text-sm font-normal transition-colors disabled:cursor-not-allowed ${
                      isPasswordValid()
                        ? 'bg-black text-white hover:bg-gray-800'
                        : 'bg-[#05050580] text-white hover:bg-[#050505] disabled:bg-gray-400'
                    }`}
                  >
                    {isUpdatingPassword ? 'Updating...' : 'Update'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

       {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-lg 2xl:max-w-xl 4xl:max-w-2xl w-full mx-4">
            <div className="mx-4 sm:mx-4 xl:mx-6 2xl:mx-6 4xl:mx-12 py-3 sm:py-3 xl:py-4 2xl:py-4 4xl:py-6">
              {/* Header */}
              <div className="flex items-center justify-between mt-4 mb-4">
                <h3 className="text-sm sm:text-base xl:text-xl 2xl:text-2xl 3xl:text-2xl 4xl:text-3xl font-semibold font-inter text-[#070707] text-left">
                  Confirm Logout
                </h3>
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5 3xl:w-6 3xl:h-6" />
                </button>
              </div>

              {/* Separator */}
              <div className="border-t border-gray-200 mb-4" />

              {/* Description */}
              <p className="text-[#070707] text-sm sm:text-sm xl:text-base 2xl:text-base 3xl:text-lg 4xl:text-xl font-inter mb-10">
                Are you sure you want to logout? You will need to login again to access the dashboard.
              </p>

              {/* Separator */}
              <div className="border-t border-gray-200 mb-6" />

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  className="w-full sm:w-auto text-xs sm:text-sm xl:text-base 2xl:text-base 3xl:text-xl 4xl:text-2xl px-4 sm:px-6 xl:px-8 2xl:px-10 4xl:px-12 py-2 sm:py-2 xl:py-2 2xl:py-3 4xl:py-4 border border-[#050505] bg-white rounded-lg text-[#050505] font-normal"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full sm:w-auto text-xs sm:text-sm xl:text-base 2xl:text-base 3xl:text-xl 4xl:text-2xl px-4 sm:px-6 xl:px-8 2xl:px-10 4xl:px-12 py-2 sm:py-2 xl:py-2 2xl:py-3 4xl:py-4 bg-black text-white rounded-lg  font-normal"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SettingsPage;